import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminDashboard.css';
import TagsManagement from '../components/TagsManagement';
import AutomationRules from '../components/AutomationRules';
import TemplatesManagement from '../components/TemplatesManagement';
import ExportDialog from '../components/ExportDialog';
import ApplicationTimeline from '../components/ApplicationTimeline';
import CommentTemplateSelector from '../components/CommentTemplateSelector';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import PerformanceChart from '../components/PerformanceChart';
import ChatConversationsJournal from '../components/ChatConversationsJournal';
import AISettings from '../components/AISettings';
import AIPriceCatalog from '../components/AIPriceCatalog';

const API = '/api/cms';
const SL = { new: 'Новая', in_progress: 'В работе', completed: 'Завершена', cancelled: 'Отменена' };
const PL = { low: 'Низкий', medium: 'Средний', high: 'Высокий', critical: 'Критический' };
const PR_COLORS = { low: '#22c55e', medium: '#3b82f6', high: '#f59e0b', critical: '#ef4444' };

const AdminDashboard = () => {
  const [tab, setTab] = useState('applications');
  const [subTab, setSubTab] = useState('all');
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [automationRules, setAutomationRules] = useState([]);
  const [commentTemplates, setCommentTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);
  const [msg, setMsg] = useState('');
  const [commentText, setCommentText] = useState({});
  const [editedApps, setEditedApps] = useState({});

  // Массовые операции
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkComment, setBulkComment] = useState('');

  // Фильтры
  const [filterPriority, setFilterPriority] = useState('');
  const [filterTags, setFilterTags] = useState('');
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  const [editingUser, setEditingUser] = useState(null);

  const [settings, setSettings] = useState({
    telegram: { botToken: '', chatId: '', enabled: false },
    email: { enabled: false, host: 'smtp.gmail.com', port: 587, secure: false, user: '', password: '', recipients: [] },
    autoAssign: { mode: 'admin', targetRoles: ['admin', 'superadmin'] },
    site: { title: '', phone: '', phone2: '', email: '', address: '', workHours: '', emergencyHours: '' }
  });
  const [newRecipient, setNewRecipient] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testChatId, setTestChatId] = useState('');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templateAppId, setTemplateAppId] = useState(null);

  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };

  const showMsg = useCallback((text) => {
    setMsg(text);
    setTimeout(() => setMsg(''), 4000);
  }, []);

  useEffect(() => { loadData(); }, [tab, subTab]);

  const loadData = async () => {
    try {
      if (tab === 'applications') {
        await loadApplications();
        const [s, u, t] = await Promise.all([
          axios.get(`${API}/applications/stats`, { headers: h }),
          axios.get(`${API}/users`, { headers: h }),
          axios.get(`${API}/tags`, { headers: h })
        ]);
        setStats(s.data);
        setUsers(u.data);
        setTags(t.data);
      } else if (tab === 'users') {
        const u = await axios.get(`${API}/users`, { headers: h });
        setUsers(u.data);
      } else if (tab === 'settings') {
        const r = await axios.get(`${API}/settings`, { headers: h });
        setSettings(r.data);
      } else if (tab === 'analytics') {
        const a = await axios.get(`${API}/analytics?period=7d`, { headers: h });
        setAnalytics(a.data);
      } else if (tab === 'tags') {
        const t = await axios.get(`${API}/tags`, { headers: h });
        setTags(t.data);
      } else if (tab === 'automation') {
        const [r, u] = await Promise.all([
          axios.get(`${API}/automation/rules`, { headers: h }),
          axios.get(`${API}/users`, { headers: h })
        ]);
        setAutomationRules(r.data);
        setUsers(u.data);
      } else if (tab === 'templates') {
        const t = await axios.get(`${API}/templates`, { headers: h });
        setCommentTemplates(t.data);
      }
    } catch (e) { setMsg('Ошибка: ' + (e.response?.data?.message || e.message)); }
  };

  const refreshStats = async () => {
    try {
      const s = await axios.get(`${API}/applications/stats`, { headers: h });
      setStats(s.data);
    } catch { /* ignore */ }
  };

  const openPrintWindow = (html) => {
    const w = window.open('', '_blank');
    if (!w) {
      showMsg('⚠️ Разрешите всплывающие окна для печати');
      return;
    }
    w.document.write(html);
    w.document.close();
  };

  const printApplication = async (appId) => {
    try {
      const res = await axios.get(`${API}/export/applications/${appId}/print`, { headers: h, responseType: 'text' });
      openPrintWindow(res.data);
    } catch (e) {
      showMsg(e.response?.data?.message || 'Ошибка печати');
    }
  };

  const printSelectedApplications = async () => {
    if (!selectedIds.length) {
      showMsg('⚠️ Выберите заявки для печати');
      return;
    }
    try {
      const res = await axios.get(`${API}/export/applications/print?ids=${selectedIds.join(',')}`, { headers: h, responseType: 'text' });
      openPrintWindow(res.data);
    } catch (e) {
      showMsg(e.response?.data?.message || 'Ошибка печати');
    }
  };

  const loadApplications = async () => {
    const params = new URLSearchParams();
    params.append('limit', '200');
    if (subTab === 'mine') params.append('mine', 'true');
    if (search) params.append('search', search);
    if (filterPriority) params.append('priority', filterPriority);
    if (filterTags) params.append('tags', filterTags);
    if (filterOverdue) params.append('overdue', 'true');
    params.append('sortBy', sortBy);
    params.append('sortDir', sortDir);

    const a = await axios.get(`${API}/applications?${params}`, { headers: h });
    setApps(a.data.applications || []);
  };

  useEffect(() => {
    if (tab === 'applications') {
      const timer = setTimeout(loadApplications, 300);
      return () => clearTimeout(timer);
    }
  }, [search, filterPriority, filterTags, filterOverdue, sortBy, sortDir, subTab]);

  const startEdit = (id, field, value) => {
    setEditedApps(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value }
    }));
  };

  const applyChanges = async (id) => {
    const edit = editedApps[id];
    if (!edit) return;
    const app = apps.find(a => a._id === id);
    const statusChanged = edit.status && edit.status !== app.status;
    const assigneeChanged = edit.assignedTo !== undefined && edit.assignedTo !== (app.assignedTo?._id || '');
    const hasComment = edit.comment?.trim();
    if ((statusChanged || assigneeChanged) && !hasComment) {
      setMsg('⚠️ При смене статуса или ответственного обязателен комментарий');
      return;
    }
    try {
      const res = await axios.put(`${API}/applications/${id}`, {
        status: edit.status || app.status,
        assignedTo: edit.assignedTo !== undefined ? edit.assignedTo : (app.assignedTo?._id || null),
        priority: edit.priority || app.priority,
        comment: edit.comment || '',
        tags: edit.tags !== undefined ? edit.tags : undefined,
        dueDate: edit.dueDate !== undefined ? edit.dueDate : undefined
      }, { headers: h });
      setApps(prev => prev.map(a => (a._id === id ? res.data : a)));
      setEditedApps(prev => { const n = { ...prev }; delete n[id]; return n; });
      setCommentText(prev => ({ ...prev, [id]: '' }));
      await refreshStats();
      showMsg('✅ Заявка обновлена');
    } catch (e) { setMsg(e.response?.data?.message || 'Ошибка'); }
  };

  const cancelEdit = (id) => {
    setEditedApps(prev => { const n = { ...prev }; delete n[id]; return n; });
    setCommentText(prev => ({ ...prev, [id]: '' }));
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Удалить заявку?')) return;
    try {
      await axios.delete(`${API}/applications/${id}`, { headers: h });
      setApps(prev => prev.filter(a => a._id !== id));
      setSelectedIds(prev => prev.filter(i => i !== id));
      await refreshStats();
      showMsg('Заявка удалена');
    } catch (e) { setMsg(e.response?.data?.message || 'Ошибка'); }
  };

  // Массовые операции
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(a => a._id));
    }
  };

  const executeBulkAction = async () => {
    if (!selectedIds.length || !bulkAction) {
      showMsg('⚠️ Выберите заявки и действие');
      return;
    }

    let value = bulkValue;
    if (bulkAction === 'addTags' || bulkAction === 'removeTags') {
      value = bulkValue.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
      if (!value.length) {
        showMsg('⚠️ Укажите теги через запятую');
        return;
      }
    }

    if ((bulkAction === 'setStatus' || bulkAction === 'assignTo') && !bulkComment.trim()) {
      showMsg('⚠️ Укажите комментарий для массового действия');
      return;
    }

    try {
      const res = await axios.post(`${API}/applications/bulk`, {
        ids: selectedIds,
        action: bulkAction,
        value,
        comment: bulkComment
      }, { headers: h });

      showMsg(res.data.message);
      setSelectedIds([]);
      setShowBulkPanel(false);
      setBulkAction('');
      setBulkValue('');
      setBulkComment('');
      await loadApplications();
      await refreshStats();
    } catch (e) {
      showMsg(e.response?.data?.message || 'Ошибка массового обновления');
    }
  };

  const createUser = async () => {
    try {
      await axios.post(`${API}/users`, newUser, { headers: h });
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      loadData(); showMsg('Пользователь создан');
    } catch (e) { setMsg(e.response?.data?.message || 'Ошибка'); }
  };

  const updateUser = async (id, data) => {
    try { await axios.put(`${API}/users/${id}`, data, { headers: h }); setEditingUser(null); loadData(); showMsg('Обновлён'); }
    catch { setMsg('Ошибка'); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Удалить?')) return;
    try { await axios.delete(`${API}/users/${id}`, { headers: h }); loadData(); showMsg('Удалён'); }
    catch (e) { setMsg(e.response?.data?.message || 'Ошибка'); }
  };

  const saveSettings = async () => {
    try { await axios.put(`${API}/settings`, settings, { headers: h }); showMsg('Сохранено'); }
    catch { setMsg('Ошибка'); }
  };

  const testTelegram = async () => {
    try { await axios.post(`${API}/settings/test-telegram`, { chatId: testChatId || settings.telegram.chatId }, { headers: h }); showMsg('Отправлено'); }
    catch { setMsg('Ошибка TG'); }
  };

  const testEmailFunc = async () => {
    try { await axios.post(`${API}/settings/test-email`, { recipient: testEmail }, { headers: h }); showMsg('Отправлено'); }
    catch { setMsg('Ошибка Email'); }
  };

  const addRecipient = () => {
    if (newRecipient && !settings.email.recipients.includes(newRecipient)) {
      setSettings({ ...settings, email: { ...settings.email, recipients: [...settings.email.recipients, newRecipient] } });
      setNewRecipient('');
    }
  };

  const filtered = apps;

  const tabs = [
    { id: 'applications', label: '📋 Заявки' },
    { id: 'users', label: '👥 Пользователи' },
    { id: 'tags', label: '🏷️ Теги' },
    { id: 'automation', label: '🤖 Автоматизация' },
    { id: 'templates', label: '📝 Шаблоны' },
    { id: 'ai', label: '🤖 AI Чат' },
    { id: 'price', label: '💰 Прайс AI' },
    { id: 'settings', label: '⚙️ Настройки' },
    { id: 'analytics', label: '📈 Аналитика' }
  ];

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Панель администратора — СЕТЬМОНТАЖ</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <a href="/dashboard" className="btn btn-outline">🏠 Личный кабинет</a>
          <a href="/" className="btn btn-outline">🌐 На сайт</a>
        </div>
      </div>

      <div className="admin-tabs">
        {tabs.map(t => (<button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>))}
      </div>

      {msg && <div className="alert">{msg}<button onClick={() => setMsg('')}>✕</button></div>}

      <div className="admin-content">
        {tab === 'applications' && (
          <div className="applications-tab">
            <div className="sub-tabs">
              <button className={`sub-tab ${subTab === 'all' ? 'active' : ''}`} onClick={() => setSubTab('all')}>📋 Все заявки ({stats?.total || 0})</button>
              <button className={`sub-tab ${subTab === 'mine' ? 'active' : ''}`} onClick={() => setSubTab('mine')}>👤 Мои заявки</button>
            </div>

            {stats && (
              <div className="stats-grid">
                <div className="stat-card"><span className="stat-value">{stats.total}</span><span className="stat-label">Всего</span></div>
                <div className="stat-card new"><span className="stat-value">{stats.new}</span><span className="stat-label">Новых</span></div>
                <div className="stat-card progress"><span className="stat-value">{stats.inProgress}</span><span className="stat-label">В работе</span></div>
                <div className="stat-card done"><span className="stat-value">{stats.completed}</span><span className="stat-label">Завершено</span></div>
                <div className="stat-card overdue"><span className="stat-value">{stats.overdue || 0}</span><span className="stat-label">Просрочено</span></div>
                {stats.slaCompliance !== null && (
                  <div className="stat-card"><span className="stat-value">{stats.slaCompliance}%</span><span className="stat-label">SLA</span></div>
                )}
              </div>
            )}

            <div className="apps-controls">
              <input type="text" className="search-input" placeholder="🔍 Поиск по имени, телефону, email..." value={search} onChange={e => setSearch(e.target.value)} />
              <button className={`btn-sm ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}>🔽 Фильтры</button>
              <div className="sort-controls">
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                  <option value="createdAt">По дате</option>
                  <option value="priority">По приоритету</option>
                  <option value="status">По статусу</option>
                  <option value="dueDate">По дедлайну</option>
                  <option value="name">По имени</option>
                </select>
                <button className="btn-sm" onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}>{sortDir === 'desc' ? '↓' : '↑'}</button>
              </div>
              <button className="btn-sm" onClick={() => { setShowBulkPanel(!showBulkPanel); setSelectedIds([]); }}>
                ☑️ Массовые {selectedIds.length > 0 && `(${selectedIds.length})`}
              </button>
              <button className="btn-sm" onClick={() => setShowExportDialog(true)}>
                📤 Экспорт
              </button>
              <button className="btn-sm" onClick={printSelectedApplications} disabled={!selectedIds.length}>
                🖨️ Печать
              </button>
            </div>

            {showFilters && (
              <div className="filters-panel">
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Приоритет:</label>
                    <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                      <option value="">Все</option>
                      <option value="low">Низкий</option>
                      <option value="medium">Средний</option>
                      <option value="high">Высокий</option>
                      <option value="critical">Критический</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Теги:</label>
                    <input type="text" placeholder="через запятую" value={filterTags} onChange={e => setFilterTags(e.target.value)} />
                  </div>
                  <div className="filter-group checkbox">
                    <label><input type="checkbox" checked={filterOverdue} onChange={e => setFilterOverdue(e.target.checked)} /> Только просроченные</label>
                  </div>
                  <button className="btn-sm" onClick={() => { setFilterPriority(''); setFilterTags(''); setFilterOverdue(false); }}>✕ Сбросить</button>
                </div>
              </div>
            )}

            {showBulkPanel && (
              <div className="bulk-panel">
                <div className="bulk-header">
                  <label><input type="checkbox" checked={selectedIds.length === filtered.length && filtered.length > 0} onChange={toggleSelectAll} /> Выбрать все ({selectedIds.length}/{filtered.length})</label>
                </div>
                <div className="bulk-actions">
                  <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}>
                    <option value="">Выберите действие</option>
                    <option value="setStatus">Изменить статус</option>
                    <option value="setPriority">Изменить приоритет</option>
                    <option value="assignTo">Назначить ответственного</option>
                    <option value="addTags">Добавить теги</option>
                    <option value="removeTags">Удалить теги</option>
                    <option value="setDueDate">Установить дедлайн</option>
                  </select>
                  {bulkAction === 'setStatus' && (
                    <select value={bulkValue} onChange={e => setBulkValue(e.target.value)}>
                      <option value="new">Новая</option>
                      <option value="in_progress">В работе</option>
                      <option value="completed">Завершена</option>
                      <option value="cancelled">Отменена</option>
                    </select>
                  )}
                  {bulkAction === 'setPriority' && (
                    <select value={bulkValue} onChange={e => setBulkValue(e.target.value)}>
                      <option value="low">Низкий</option>
                      <option value="medium">Средний</option>
                      <option value="high">Высокий</option>
                      <option value="critical">Критический</option>
                    </select>
                  )}
                  {bulkAction === 'assignTo' && (
                    <select value={bulkValue} onChange={e => setBulkValue(e.target.value)}>
                      <option value="">Выберите пользователя</option>
                      {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                    </select>
                  )}
                  {(bulkAction === 'addTags' || bulkAction === 'removeTags') && (
                    <input type="text" placeholder="теги через запятую" value={bulkValue} onChange={e => setBulkValue(e.target.value)} />
                  )}
                  {bulkAction === 'setDueDate' && (
                    <input type="datetime-local" value={bulkValue} onChange={e => setBulkValue(e.target.value)} />
                  )}
                  <input type="text" className="bulk-comment-input" placeholder="Комментарий (обязателен для статуса/назначения)" value={bulkComment} onChange={e => setBulkComment(e.target.value)} />
                  <button className="btn btn-primary btn-sm" onClick={executeBulkAction} disabled={!selectedIds.length || !bulkAction}>Применить</button>
                </div>
              </div>
            )}

            <div className="apps-grid">
              {filtered.map(app => {
                const edit = editedApps[app._id] || {};
                const hasChanges = Object.keys(edit).length > 0;
                const currentStatus = edit.status || app.status;
                const currentPriority = edit.priority || app.priority;
                const currentAssignee = edit.assignedTo !== undefined ? edit.assignedTo : (app.assignedTo?._id || '');
                const isSelected = selectedIds.includes(app._id);
                const isOverdue = app.isOverdue && app.status !== 'completed' && app.status !== 'cancelled';
                const dueDate = app.dueDate ? new Date(app.dueDate) : null;
                const isUrgent = app.isUrgent;

                return (
                  <div key={app._id} className={`app-card-admin ${expandedId === app._id ? 'expanded' : ''} ${hasChanges ? 'editing' : ''} ${isOverdue ? 'overdue' : ''} ${isSelected ? 'selected' : ''}`}>
                    <div className="app-card-header" onClick={() => setExpandedId(expandedId === app._id ? null : app._id)}>
                      <div className="app-card-header-left">
                        {showBulkPanel && (
                          <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(app._id)} onClick={e => e.stopPropagation()} />
                        )}
                        <span className={`status-badge status-${currentStatus}`}>{SL[currentStatus] || currentStatus}</span>
                        <span className="priority-badge" style={{ background: PR_COLORS[currentPriority] || '#3b82f6' }}>
                          {PL[currentPriority] || currentPriority}
                        </span>
                        {isOverdue && <span className="overdue-badge">⚠️ Просрочено</span>}
                        {isUrgent && !isOverdue && <span className="urgent-badge">🔥 Срочно</span>}
                      </div>
                      <span className="app-date">{new Date(app.createdAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="app-card-body">
                      <div className="app-card-top-row">
                        <h3>{app.name}</h3>
                        <button className="btn-sm" onClick={() => printApplication(app._id)} title="Печать">🖨️</button>
                        <button className="btn-sm btn-danger delete-btn" onClick={() => deleteApplication(app._id)} title="Удалить">🗑️</button>
                      </div>
                      <p className="app-contact">📞 <a href={`tel:${app.phone}`}>{app.phone}</a> | ✉️ <a href={`mailto:${app.email}`}>{app.email}</a></p>
                      <p className="app-service">🔧 {app.service || 'Не указана'}</p>

                      {dueDate && (
                        <p className={`app-due-date ${isOverdue ? 'overdue-text' : ''}`}>
                          📅 Дедлайн: {dueDate.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {app.slaPercentage !== null && <span className="sla-bar"><span className="sla-fill" style={{ width: `${Math.min(100, app.slaPercentage)}%`, background: app.slaPercentage > 80 ? '#ef4444' : app.slaPercentage > 50 ? '#f59e0b' : '#22c55e' }}></span></span>}
                        </p>
                      )}

                      {app.tags && app.tags.length > 0 && (
                        <div className="app-tags">
                          {app.tags.map((tag, i) => {
                            const tagData = tags.find(t => t.name === tag);
                            return <span key={i} className="tag-badge" style={{ background: tagData?.color || '#e2e8f0', color: tagData?.color ? '#fff' : '#475569' }}>{tagData?.displayName || tag}</span>;
                          })}
                        </div>
                      )}

                      <div className="app-assign">
                        <label>👨‍💼 Ответственный:</label>
                        <select value={currentAssignee} onChange={e => startEdit(app._id, 'assignedTo', e.target.value)}>
                          {users.map(u => (
                            <option key={u._id} value={u._id}>{u.name} ({u.email}) {u.workStatus === 'dayoff' ? '🔴' : '🟢'}</option>
                          ))}
                        </select>
                      </div>

                      <div className="app-priority-edit">
                        <label>⚡ Приоритет:</label>
                        <select value={currentPriority} onChange={e => startEdit(app._id, 'priority', e.target.value)}>
                          <option value="low">Низкий</option>
                          <option value="medium">Средний</option>
                          <option value="high">Высокий</option>
                          <option value="critical">Критический</option>
                        </select>
                      </div>

                      <div className="app-tags-edit">
                        <label>🏷️ Теги:</label>
                        <input type="text" placeholder="через запятую" value={edit.tags !== undefined ? edit.tags.join(', ') : (app.tags || []).join(', ')} onChange={e => startEdit(app._id, 'tags', e.target.value.split(',').map(t => t.trim().toLowerCase()).filter(Boolean))} />
                      </div>

                      <div className="app-due-edit">
                        <label>📅 Дедлайн:</label>
                        <input type="datetime-local" value={edit.dueDate || (app.dueDate ? new Date(app.dueDate).toISOString().slice(0, 16) : '')} onChange={e => startEdit(app._id, 'dueDate', e.target.value)} />
                      </div>

                      {app.message && <div className={`app-message ${expandedId === app._id ? 'expanded' : 'collapsed'}`}>💬 {app.message}</div>}
                      {expandedId === app._id && app.message?.length > 100 && <button className="expand-btn" onClick={() => setExpandedId(null)}>Свернуть</button>}

                      {app.comments && app.comments.length > 0 && (
                        <div className="app-comments">
                          <h4>📝 История изменений</h4>
                          {app.comments.map((c, i) => (
                            <div key={i} className="comment-item">
                              <div className="comment-header">
                                <span className="comment-user">{c.userName || 'Система'}</span>
                                <span className="comment-date">{new Date(c.createdAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                              {c.fromStatus && c.toStatus && (
                                <div className="comment-status-change">
                                  <span className={`status-badge status-${c.fromStatus}`}>{SL[c.fromStatus]}</span>
                                  <span className="arrow">→</span>
                                  <span className={`status-badge status-${c.toStatus}`}>{SL[c.toStatus]}</span>
                                </div>
                              )}
                              {c.assigneeChanged && (
                                <div className="comment-assignee-change">
                                  👨‍💼 {c.assigneeChanged.from === 'не назначен' ? 'Не назначен' : users.find(u => String(u._id) === c.assigneeChanged.from)?.name || c.assigneeChanged.from} → {users.find(u => String(u._id) === c.assigneeChanged.to)?.name || c.assigneeChanged.to}
                                </div>
                              )}
                              <p className="comment-text">{c.text}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {expandedId === app._id && (
                        <ApplicationTimeline application={app} users={users} />
                      )}

                      <div className="app-status-change">
                        <div className="status-row">
                          <select value={currentStatus} onChange={e => startEdit(app._id, 'status', e.target.value)}>
                            <option value="new">Новая</option>
                            <option value="in_progress">В работе</option>
                            <option value="completed">Завершена</option>
                            <option value="cancelled">Отменена</option>
                          </select>
                          <button
                            className="btn-sm template-btn"
                            onClick={(e) => { e.stopPropagation(); setShowTemplateSelector(true); setTemplateAppId(app._id); }}
                            title="Выбрать шаблон комментария"
                          >
                            📝
                          </button>
                        </div>
                        <div className="comment-row">
                          <input
                            type="text"
                            className="comment-input"
                            placeholder="💬 Комментарий (обязателен при смене статуса/ответственного)"
                            value={edit.comment || commentText[app._id] || ''}
                            onChange={e => startEdit(app._id, 'comment', e.target.value)}
                          />
                        </div>
                        {hasChanges && (
                          <div className="edit-actions">
                            <button className="btn btn-primary btn-apply" onClick={() => applyChanges(app._id)}>✅ Применить</button>
                            <button className="btn btn-outline-dark btn-cancel" onClick={() => cancelEdit(app._id)}>✕ Отмена</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'users' && (
          <div className="users-tab">
            <h2>Управление пользователями</h2>
            <div className="create-user-form">
              <h3>Создать пользователя</h3>
              <div className="form-row">
                <input type="text" placeholder="Имя" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} />
                <input type="email" placeholder="Email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
                <input type="password" placeholder="Пароль" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                  <option value="user">Пользователь</option>
                  <option value="admin">Администратор</option>
                  <option value="superadmin">Суперадмин</option>
                </select>
                <button className="btn btn-primary" onClick={createUser}>➕ Создать</button>
              </div>
            </div>
            <table className="users-table">
              <thead><tr><th>Имя</th><th>Email</th><th>Роль</th><th>Статус</th><th>Авто-назнач.</th><th>Подтверждён</th><th>Дата</th><th>Действия</th></tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id}>
                    {editingUser === u._id ? (<>
                      <td><input type="text" defaultValue={u.name} id={`un-${u._id}`} /></td>
                      <td>{u.email}</td>
                      <td><select id={`ur-${u._id}`} defaultValue={u.role}><option value="user">Пользователь</option><option value="admin">Администратор</option><option value="superadmin">Суперадмин</option></select></td>
                      <td><select id={`uw-${u._id}`} defaultValue={u.workStatus || 'working'}><option value="working">🟢 Работает</option><option value="dayoff">🔴 Выходной</option></select></td>
                      <td><input type="checkbox" id={`ua-${u._id}`} defaultChecked={u.autoAssignEnabled !== false} /></td>
                      <td><input type="checkbox" id={`uv-${u._id}`} defaultChecked={u.isEmailVerified} /> Email</td>
                      <td>{new Date(u.createdAt).toLocaleDateString('ru-RU')}</td>
                      <td><button className="btn-sm" onClick={() => updateUser(u._id, { name: document.getElementById(`un-${u._id}`).value, role: document.getElementById(`ur-${u._id}`).value, workStatus: document.getElementById(`uw-${u._id}`).value, autoAssignEnabled: document.getElementById(`ua-${u._id}`).checked, isEmailVerified: document.getElementById(`uv-${u._id}`).checked })}>💾</button><button className="btn-sm" onClick={() => setEditingUser(null)}>✕</button></td>
                    </>) : (<>
                      <td>{u.name}</td><td>{u.email}</td><td><span className={`role-badge role-${u.role}`}>{u.role}</span></td>
                      <td><span className={`work-badge ${u.workStatus || 'working'}`}>{(u.workStatus || 'working') === 'working' ? '🟢 Работает' : '🔴 Выходной'}</span></td>
                      <td>{u.autoAssignEnabled !== false ? '✅' : '❌'}</td><td>{u.isEmailVerified ? '✅' : '❌'}</td>
                      <td>{new Date(u.createdAt).toLocaleDateString('ru-RU')}</td>
                      <td><button className="btn-sm" onClick={() => setEditingUser(u._id)}>✏️</button><button className="btn-sm btn-danger" onClick={() => deleteUser(u._id)}>🗑️</button></td>
                    </>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'settings' && (
          <div className="settings-page">
            <h2>Настройки</h2>
            <div className="settings-section">
              <h3>📱 Telegram-бот</h3>
              <div className="form-group"><label>Bot Token</label><input type="text" value={settings.telegram.botToken} onChange={e => setSettings({ ...settings, telegram: { ...settings.telegram, botToken: e.target.value } })} /></div>
              <div className="form-group"><label>Chat ID</label><input type="text" value={settings.telegram.chatId} onChange={e => setSettings({ ...settings, telegram: { ...settings.telegram, chatId: e.target.value } })} /></div>
              <div className="form-group checkbox"><label><input type="checkbox" checked={settings.telegram.enabled} onChange={e => setSettings({ ...settings, telegram: { ...settings.telegram, enabled: e.target.checked } })} /> Включить</label></div>
              <div className="form-group"><label>Тест Chat ID</label><input type="text" value={testChatId} onChange={e => setTestChatId(e.target.value)} /><button className="btn btn-sm" onClick={testTelegram}>📤 Тест</button></div>
            </div>
            <div className="settings-section">
              <h3>📧 Email</h3>
              <div className="form-group"><label>SMTP</label><input type="text" value={settings.email.host} onChange={e => setSettings({ ...settings, email: { ...settings.email, host: e.target.value } })} /></div>
              <div className="form-group"><label>Порт</label><input type="number" value={settings.email.port} onChange={e => setSettings({ ...settings, email: { ...settings.email, port: parseInt(e.target.value) } })} /></div>
              <div className="form-group"><label>Email</label><input type="text" value={settings.email.user} onChange={e => setSettings({ ...settings, email: { ...settings.email, user: e.target.value } })} /></div>
              <div className="form-group"><label>Пароль</label><input type="password" value={settings.email.password} onChange={e => setSettings({ ...settings, email: { ...settings.email, password: e.target.value } })} placeholder="Не меняйте если не нужно" /></div>
              <div className="form-group checkbox"><label><input type="checkbox" checked={settings.email.enabled} onChange={e => setSettings({ ...settings, email: { ...settings.email, enabled: e.target.checked } })} /> Включить</label></div>
              <div className="form-group"><label>Получатели</label>
                <div className="recipients-list">{settings.email.recipients.map((r, i) => (<span key={i} className="recipient-tag">{r} <button onClick={() => setSettings({ ...settings, email: { ...settings.email, recipients: settings.email.recipients.filter(x => x !== r) } })}>×</button></span>))}</div>
                <div className="add-recipient"><input type="email" value={newRecipient} onChange={e => setNewRecipient(e.target.value)} /><button className="btn btn-sm" onClick={addRecipient}>+</button></div>
              </div>
              <div className="form-group"><label>Тест email</label><input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} /><button className="btn btn-sm" onClick={testEmailFunc}>📤</button></div>
            </div>
            <div className="settings-section">
              <h3>🔄 Авто-назначение</h3>
              <div className="form-group">
                <label>Режим</label>
                <select value={settings.autoAssign.mode} onChange={e => setSettings({ ...settings, autoAssign: { ...settings.autoAssign, mode: e.target.value } })}>
                  <option value="admin">На первого админа</option>
                  <option value="round_robin">По очереди</option>
                  <option value="all">На всех сразу</option>
                </select>
              </div>
              <div className="form-group">
                <label>Назначать на роли</label>
                <div className="checkbox-group">
                  <label><input type="checkbox" checked={settings.autoAssign.targetRoles.includes('admin')} onChange={e => { const roles = e.target.checked ? [...settings.autoAssign.targetRoles, 'admin'] : settings.autoAssign.targetRoles.filter(r => r !== 'admin'); setSettings({ ...settings, autoAssign: { ...settings.autoAssign, targetRoles: roles } }); }} /> Администраторы</label>
                  <label><input type="checkbox" checked={settings.autoAssign.targetRoles.includes('superadmin')} onChange={e => { const roles = e.target.checked ? [...settings.autoAssign.targetRoles, 'superadmin'] : settings.autoAssign.targetRoles.filter(r => r !== 'superadmin'); setSettings({ ...settings, autoAssign: { ...settings.autoAssign, targetRoles: roles } }); }} /> Суперадмины</label>
                </div>
              </div>
            </div>
            <div className="settings-section">
              <h3>🌐 Сайт</h3>
              <div className="form-group"><label>Телефон 1</label><input type="text" value={settings.site.phone} onChange={e => setSettings({ ...settings, site: { ...settings.site, phone: e.target.value } })} /></div>
              <div className="form-group"><label>Телефон 2</label><input type="text" value={settings.site.phone2} onChange={e => setSettings({ ...settings, site: { ...settings.site, phone2: e.target.value } })} /></div>
              <div className="form-group"><label>Email</label><input type="text" value={settings.site.email} onChange={e => setSettings({ ...settings, site: { ...settings.site, email: e.target.value } })} /></div>
              <div className="form-group"><label>Адрес</label><input type="text" value={settings.site.address} onChange={e => setSettings({ ...settings, site: { ...settings.site, address: e.target.value } })} /></div>
              <div className="form-group"><label>Режим работы</label><input type="text" value={settings.site.workHours} onChange={e => setSettings({ ...settings, site: { ...settings.site, workHours: e.target.value } })} /></div>
              <div className="form-group"><label>Аварийная</label><input type="text" value={settings.site.emergencyHours} onChange={e => setSettings({ ...settings, site: { ...settings.site, emergencyHours: e.target.value } })} /></div>
            </div>
            <button className="btn btn-primary" onClick={saveSettings}>💾 Сохранить</button>
          </div>
        )}

        {tab === 'tags' && (
          <TagsManagement
            tags={tags}
            setTags={setTags}
            token={token}
            showMsg={showMsg}
          />
        )}

        {tab === 'automation' && (
          <AutomationRules
            rules={automationRules}
            setRules={setAutomationRules}
            users={users}
            token={token}
            showMsg={showMsg}
          />
        )}

        {tab === 'templates' && (
          <TemplatesManagement
            templates={commentTemplates}
            setTemplates={setCommentTemplates}
            token={token}
            showMsg={showMsg}
          />
        )}

        {showTemplateSelector && (
          <CommentTemplateSelector
            token={token}
            onSelect={(template) => {
              if (templateAppId) {
                startEdit(templateAppId, 'comment', template.content);
              }
            }}
            onClose={() => { setShowTemplateSelector(false); setTemplateAppId(null); }}
          />
        )}

        {showExportDialog && (
          <ExportDialog
            apps={apps}
            onClose={() => setShowExportDialog(false)}
            showMsg={showMsg}
            token={token}
            currentFilters={{
              priority: filterPriority,
              tags: filterTags
            }}
          />
        )}

        {tab === 'ai' && (
          <AISettings
            token={token}
            showMsg={showMsg}
          />
        )}

        {tab === 'price' && (
          <AIPriceCatalog token={token} showMsg={showMsg} />
        )}

        {tab === 'analytics' && (
          <>
            <AnalyticsDashboard analytics={analytics} stats={stats} />
            <PerformanceChart applications={apps} users={users} />
            <ChatConversationsJournal token={token} />
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
